import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
interface RequirementView {
  readonly requirementId: string;
  readonly requirementCode: string;
  readonly title: string;
  readonly description: string | null;
  readonly requirementType: string;
  readonly businessRules: string | null;
  readonly acceptanceCriteria: string | null;
  readonly priority: string;
  readonly status: string;
  readonly currentVersionNo: number;
  readonly isTraceable: boolean;
}

interface TraceRow {
  readonly traceabilityId: string;
  readonly traceCode: string;
  readonly traceStage: string;
  readonly linkedEntityType: string | null;
  readonly linkedEntityRef: string | null;
  readonly status: string;
}

/**
 * GR_M31-M35 §L33.03 / §M — requirement (user story) detail viewer
 * (`/admin/pm/requirements/:id`). Renders a single business requirement (T435)
 * with its acceptance criteria (§M L33.03 story.acceptance_criteria) and business
 * rules, plus the downstream traceability links (§M story.linked_test_case → the
 * requirement-traceability matrix, T437) so a BA can see which features / test
 * cases satisfy the story.
 */
export function RequirementDetailPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [req, setReq] = useState<RequirementView | null>(null);
  const [traces, setTraces] = useState<TraceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!id) { setError(t('m33.reqDetail.noId')); setLoading(false); return; }
    setLoading(true);
    apiRequest<{ data: RequirementView }>(`/v1/pm/requirements/${id}`, { token })
      .then(async (r) => {
        if (!alive) return;
        setReq(r.data);
        setError(null);
        try {
          const t = await apiRequest<{ data: TraceRow[] }>(
            `/v1/pm/traceability?requirementId=${id}`, { token });
          if (alive) setTraces(t.data ?? []);
        } catch {
          if (alive) setTraces([]);
        }
      })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : t('m33.reqDetail.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const act = async (path: string, label: string, perm: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/requirements/${id}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m33.common.noticeLabel', { label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  if (loading) return <main aria-label="requirement-detail"><p>{t('m33.common.loading')}</p></main>;
  if (error) return <main aria-label="requirement-detail"><p role="alert">{error}</p></main>;
  if (!req) return <main aria-label="requirement-detail"><p role="alert">{t('m33.reqDetail.notFound')}</p></main>;

  return (
    <main aria-label="requirement-detail">
      <h1>{req.title}</h1>
      <dl>
        <dt>{t('m33.common.code')}</dt><dd aria-label="requirement-code">{req.requirementCode}</dd>
        <dt>{t('m33.common.type')}</dt><dd aria-label="requirement-type">{req.requirementType}</dd>
        <dt>{t('m33.common.priority')}</dt><dd aria-label="requirement-priority">{req.priority}</dd>
        <dt>{t('m33.common.status')}</dt><dd aria-label="requirement-status">{req.status}</dd>
        <dt>{t('m33.common.version')}</dt><dd aria-label="requirement-version">{req.currentVersionNo}</dd>
        <dt>{t('m33.reqDetail.traceable')}</dt><dd aria-label="requirement-traceable">{req.isTraceable ? t('m33.common.yes') : t('m33.common.no')}</dd>
      </dl>

      {req.description && <p aria-label="requirement-description">{req.description}</p>}

      <section aria-label="requirement-lifecycle">
        <h2>{t('m33.reqDetail.lifecycle')}</h2>
        {req.status === 'DRAFT' && (
          <Button variant="secondary" type="button" aria-label="submit-requirement" onClick={() => void act('submit', t('m33.reqDetail.act.submitted'), 'REQUIREMENT_MANAGE')}>{t('m33.reqDetail.btnSubmit')}</Button>
        )}
        {req.status === 'REVIEW' && (
          <Button variant="secondary" type="button" aria-label="approve-requirement" onClick={() => void act('approve', t('m33.reqDetail.act.approved'), 'REQUIREMENT_APPROVE')}>{t('m33.reqDetail.btnApprove')}</Button>
        )}
        {req.status === 'APPROVED' && (
          <Button variant="secondary" type="button" aria-label="baseline-requirement" onClick={() => void act('baseline', t('m33.reqDetail.act.baselined'), 'REQUIREMENT_APPROVE')}>{t('m33.reqDetail.btnBaseline')}</Button>
        )}
        {!['ARCHIVED', 'RETIRED'].includes(req.status) && (
          <Button variant="danger" type="button" aria-label="archive-requirement" onClick={() => void act('archive', t('m33.reqDetail.act.archived'), 'REQUIREMENT_MANAGE')}>{t('m33.reqDetail.btnArchive')}</Button>
        )}
        {actionError && <p role="alert" aria-label="requirement-action-error">{actionError}</p>}
        {notice && <p aria-label="requirement-action-notice">{notice}</p>}
      </section>

      <section aria-label="requirement-acceptance">
        <h2>{t('m33.reqDetail.acceptance')}</h2>
        <p aria-label="requirement-acceptance-criteria">{req.acceptanceCriteria ?? t('m33.reqDetail.notSpecified')}</p>
      </section>

      {req.businessRules && (
        <section aria-label="requirement-business-rules">
          <h2>{t('m33.reqDetail.businessRules')}</h2>
          <p>{req.businessRules}</p>
        </section>
      )}

      <section aria-label="requirement-traceability">
        <h2>{t('m33.reqDetail.traceHeading')}</h2>
        {traces.length > 0 ? (
          <DataTable<TraceRow>
            ariaLabel="requirement-trace-table"
            rowKey={(row) => row.traceabilityId}
            rows={traces}
            rowDataAttrs={(row) => ({ 'data-stage': row.traceStage })}
            columns={[
              { key: 'traceStage', header: t('m33.reqDetail.colStage') },
              { key: 'linkedEntityType', header: t('m33.reqDetail.colLinkedEntity'), render: (row) => row.linkedEntityType ?? '—' },
              { key: 'linkedEntityRef', header: t('m33.reqDetail.colReference'), cellAriaLabel: (row) => `trace-ref-${row.traceabilityId}`, render: (row) => row.linkedEntityRef ?? '—' },
              { key: 'status', header: t('m33.common.status') },
            ]}
          />
        ) : (
          <p aria-label="requirement-no-traces">{t('m33.reqDetail.noTraces')}</p>
        )}
      </section>
    </main>
  );
}
